import { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import type { GroupItineraryItem } from '../../types/groups';

const CHANGE_NOTICE_FAILED_MESSAGE = "Event cancelled, but the change notice couldn't be opened. Refresh to see the update.";

/**
 * Asks why before retiring an event.
 *
 * The reason is not paperwork: it is what a guest reads on their own timeline
 * where the event used to be. "Cancelled" alone reads as a mistake, and the
 * server refuses a blank one rather than writing a placeholder that would be
 * indistinguishable from something the host actually said.
 */
export default function CancelItineraryDialog({
  item,
  groupId,
  onClose,
  onCancelled,
}: {
  item: GroupItineraryItem;
  groupId: string;
  onClose: () => void;
  /**
   * Carries the server's own echo of the now-cancelled tile — the caller uses
   * it, alongside `item` (the tile as it stood before this cancellation), to
   * decide whether guests need telling. See lib/changeNotice.ts.
   */
  onCancelled: (cancelled: GroupItineraryItem) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ItineraryTab's onCancelled closes THIS dialog (sets `cancelling` to null)
  // before it runs its own post-cancel work — see the ordering check below.
  // That means by the time onCancelled can reject, our unmount is usually
  // already committed, and setState here would either no-op silently or, in
  // an async rejection landing after that commit, warn about updating an
  // unmounted component. This ref is the guard: only touch state while we
  // know we're still mounted, otherwise log instead.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const submit = async () => {
    setSaving(true);
    setError('');
    // Only the network call is guarded here. `onCancelled` runs the caller's
    // post-cancel work (ItineraryTab's offerChangeNotice + onChanged refetch)
    // — if that throws, the cancel itself already succeeded, so it must not
    // land in this catch and get reported to the host as "Could not cancel
    // that event."
    let cancelled: GroupItineraryItem;
    try {
      cancelled = await groupsApi.cancelItineraryItem(groupId, item.id, reason.trim());
    } catch (err: any) {
      setError(mutationErrorMessage(err) || 'Could not cancel that event.');
      setSaving(false);
      return;
    }
    // The cancel above already succeeded. `await` here so a REJECTED promise
    // (not just a synchronous throw) is caught too — without it, an async
    // onCancelled failure would be an unhandled rejection instead of landing
    // in this catch.
    try {
      await onCancelled(cancelled);
    } catch (err) {
      if (mountedRef.current) {
        // Distinct from the network-failure message above on purpose: the
        // cancel is done, this is only the follow-up (refetch + change-notice
        // prompt) failing. Never the "Could not cancel" text — that would lie
        // about whether the cancel itself succeeded.
        setSaving(false);
        setError(CHANGE_NOTICE_FAILED_MESSAGE);
      } else {
        // eslint-disable-next-line no-console
        console.error('CancelItineraryDialog: onCancelled failed after this dialog closed', err);
      }
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cancel “{item.title}”?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          It stays on the trip as a cancelled plan rather than disappearing, so guests who already
          arranged something around it can see what changed.
        </DialogContentText>
        <Alert severity="info" sx={{ mb: 2 }}>
          Guests see this on their timeline — the plan stays, struck through, with your reason
          beside it.
        </Alert>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <TextField
          fullWidth
          multiline
          minRows={2}
          autoFocus
          label="What should guests be told?"
          placeholder="e.g. The restaurant closed for a private event."
          value={reason}
          disabled={saving}
          onChange={(e) => setReason(e.target.value)}
          helperText="Shown to guests in place of the plan."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Keep it</Button>
        <Button color="error" variant="contained" disabled={saving || !reason.trim()} onClick={submit}>
          {saving ? 'Cancelling…' : 'Cancel event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
